import { useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectTwoFactorMaskedDestination, selectAuthStatus, selectAuthError } from '@/store/selectors/auth';
import { confirmEmailThunk } from '@/store/thunks/auth/confirmEmailThunk';
import { sendEmailCodeThunk } from '@/store/thunks/auth/sendEmailCodeThunk';
import { resendEmailCodeThunk } from '@/store/thunks/auth/resendEmailCodeThunk';
import { useCodeConfirmation } from '@/hooks/shared/useCodeConfirmation';
import { setRegistrationStep } from '@/utils/sessionStorage';

interface UseEmailConfirmationState {
  code: string;
  hasSubmitted: boolean;
  isResending: boolean;
  resendCountdown: number;
  codeError: string | undefined;
  isSubmitting: boolean;
  isVerifyDisabled: boolean;
  resendDisabled: boolean;
  maskedEmail: string;
  codeLength: number;
  globalError: string | undefined;
}

interface UseEmailConfirmationHandlers {
  handleCodeChange: (next: string) => void;
  handleSubmit: () => void;
  handleResend: () => void;
}

interface UseEmailConfirmationReturn {
  state: UseEmailConfirmationState;
  handlers: UseEmailConfirmationHandlers;
}

export function useEmailConfirmation(): UseEmailConfirmationReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const maskedDestination = useAppSelector(selectTwoFactorMaskedDestination);
  const authStatus = useAppSelector(selectAuthStatus);
  const authError = useAppSelector(selectAuthError);

  useEffect(() => {
    void dispatch(sendEmailCodeThunk());
  }, [dispatch]);

  const handleConfirm = useCallback(
    async(code: string): Promise<boolean> => {
      const action = await dispatch(confirmEmailThunk({ code }));
      const isFulfilled = confirmEmailThunk.fulfilled.match(action);
      if (isFulfilled) {
        setRegistrationStep('phoneCode');
        await navigate({ to: '/registration/verify-phone' });
      }
      return isFulfilled;
    },
    [dispatch, navigate],
  );

  const handleResend = useCallback(async(): Promise<number | undefined> => {
    const action = await dispatch(resendEmailCodeThunk());
    if (resendEmailCodeThunk.fulfilled.match(action)) {
      return action.payload.expiresInSeconds;
    }
    return undefined;
  }, [dispatch]);

  const { state, handlers } = useCodeConfirmation({
    onConfirm: handleConfirm,
    onResend: handleResend,
    maskedDestination: maskedDestination ?? '',
    loading: authStatus === 'loading',
    error: authError,
  });

  return {
    state: {
      ...state,
      maskedEmail: state.maskedDestination,
      isVerifyDisabled: state.isSubmitDisabled,
    },
    handlers,
  };
}
